import {Center, Divider, Grid, Group, Modal, ModalProps, Table, Tabs, Text, Title} from "@mantine/core";
import {compareString, CostedEntity, firstOrUndefined, ObjectCostRateModelEntity, sum} from "@ots-frontend/ots-data";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {useMemo} from "react";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {IconCoinOff} from "@tabler/icons-react";

const options: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 5
}

interface OcrmBreakdown {
  type?: string
  allocation?: number
  qty?: number
  rate?: number
  cost?: number
  ocrm: ObjectCostRateModelEntity
}

interface WellBreakdown {
  wellId: string
  wellName: string
  ocrms: OcrmBreakdown[]
}

interface RateModelBreakdown {
  rateModelId: string
  rateModelName: string
  wellBreakdown: Record<string, WellBreakdown>
  nonWellOcrms: OcrmBreakdown[]
  ocrms: ObjectCostRateModelEntity[]
  genericTimeCost?: number
  genericFreightCost?: number
  genericLoadCost?: number
  pickupDemurrageCost?: number
  deliveryDemurrageCost?: number
  baseFuelSurchargeLoadCost?: number
  percentFuelSurchargeLoadCost?: number
  siteRestrictionFreightCost?: number
  siteRestrictionLoadCost?: number
  siteRestrictionTimeCost?: number
  ticketRejectLoadCost?: number
  shiftLoadCost?: number
  splitLoadCost?: number
  hasSpecificCost?: boolean
}

const getOcrmType = (ocrm: ObjectCostRateModelEntity): string | undefined => {
  if (ocrm.billableHours && ocrm.billableHours > 0) {
    if (ocrm.pickupDemurrageCost && !ocrm.deliveryDemurrageCost) {
      return "Time (Pickup)"
    } else if (!ocrm.pickupDemurrageCost && ocrm.deliveryDemurrageCost) {
      return "Time (Delivery)"
    } else {
      return "Time"
    }
  } else if (ocrm.freightCost && ocrm.freightCost > 0) {
    return "Freight"
  } else if (ocrm.loadCost && ocrm.loadCost > 0) {
    return "Load"
  } else if (ocrm.taxableAmount && ocrm.taxableAmount > 0) {
    return "Tax"
  } else {
    return undefined
  }
}

const getOcrmQty = (ocrm: ObjectCostRateModelEntity): number | undefined => {
  if (ocrm.billableHours && ocrm.billableHours > 0) {
    return ocrm.billableHours
  } else if (ocrm.freightCost && ocrm.freightCost > 0) {
    return ocrm.billableQty
  } else if (ocrm.loadCost && ocrm.loadCost > 0) {
    return ocrm.billableQty ?? 1
  } else if (ocrm.taxableAmount && ocrm.taxableAmount > 0) {
    return ocrm.taxableAmount
  } else {
    return undefined
  }
}

const getOcrmRate = (ocrm: ObjectCostRateModelEntity): number | undefined => {
  if (ocrm.billableHours && ocrm.billableHours > 0) {
    return ocrm.timeRate
  } else if (ocrm.freightCost && ocrm.freightCost > 0) {
    return ocrm.freightRate
  } else if (ocrm.loadCost && ocrm.loadCost > 0) {
    return ocrm.loadRate
  } else if (ocrm.taxableAmount && ocrm.taxableAmount > 0) {
    return ocrm.taxRate
  } else {
    return undefined
  }
}

const getOcrmCost = (ocrm: ObjectCostRateModelEntity): number | undefined => {
  if (ocrm.billableHours && ocrm.billableHours > 0) {
    return ocrm.timeCost
  } else if (ocrm.freightCost && ocrm.freightCost > 0) {
    return ocrm.freightCost
  } else if (ocrm.loadCost && ocrm.loadCost > 0) {
    return ocrm.loadCost
  } else if (ocrm.taxableAmount && ocrm.taxableAmount > 0) {
    return ocrm.taxCost
  } else {
    return undefined
  }
}

const getOcrmBreakdown = (ocrm: ObjectCostRateModelEntity): OcrmBreakdown => {
  return {
    type: getOcrmType(ocrm),
    allocation: ocrm.wellId ? (ocrm.wellAllocation ?? 0) : undefined,
    qty: getOcrmQty(ocrm),
    rate: getOcrmRate(ocrm),
    cost: getOcrmCost(ocrm),
    ocrm: ocrm
  }
}

type CostBreakdown = Record<string, RateModelBreakdown>

const getCostBreakdown = (ocrms: ObjectCostRateModelEntity[]): CostBreakdown => {
  const breakdown: CostBreakdown = {}

  for (const ocrm of ocrms) {


    const rateModelName = ocrm.rateModelName ?? "Unknown"

    let rateModelBreakdown: RateModelBreakdown

    if (rateModelName in breakdown) {
      rateModelBreakdown = breakdown[rateModelName]
    } else {
      rateModelBreakdown = {
        rateModelId: ocrm.rateModelId,
        rateModelName: rateModelName,
        wellBreakdown: {},
        nonWellOcrms: [],
        ocrms: [],
      }
      breakdown[rateModelName] = rateModelBreakdown
    }

    if (ocrm.genericTimeCost) {
      if (!rateModelBreakdown.genericTimeCost) {
        rateModelBreakdown.genericTimeCost = 0
      }

      rateModelBreakdown.genericTimeCost += ocrm.genericTimeCost
    }

    if (ocrm.genericFreightCost) {
      if (!rateModelBreakdown.genericFreightCost) {
        rateModelBreakdown.genericFreightCost = 0
      }

      rateModelBreakdown.genericFreightCost += ocrm.genericFreightCost
    }

    if (ocrm.genericLoadCost) {
      if (!rateModelBreakdown.genericLoadCost) {
        rateModelBreakdown.genericLoadCost = 0
      }

      rateModelBreakdown.genericLoadCost += ocrm.genericLoadCost
    }

    if (ocrm.pickupDemurrageCost) {
      if (!rateModelBreakdown.pickupDemurrageCost) {
        rateModelBreakdown.pickupDemurrageCost = 0
      }

      rateModelBreakdown.pickupDemurrageCost += ocrm.pickupDemurrageCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.deliveryDemurrageCost) {
      if (!rateModelBreakdown.deliveryDemurrageCost) {
        rateModelBreakdown.deliveryDemurrageCost = 0
      }

      rateModelBreakdown.deliveryDemurrageCost += ocrm.deliveryDemurrageCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.baseFuelSurchargeLoadCost) {
      if (!rateModelBreakdown.baseFuelSurchargeLoadCost) {
        rateModelBreakdown.baseFuelSurchargeLoadCost = 0
      }

      rateModelBreakdown.baseFuelSurchargeLoadCost += ocrm.baseFuelSurchargeLoadCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.percentFuelSurchargeLoadCost) {
      if (!rateModelBreakdown.percentFuelSurchargeLoadCost) {
        rateModelBreakdown.percentFuelSurchargeLoadCost = 0
      }

      rateModelBreakdown.percentFuelSurchargeLoadCost += ocrm.percentFuelSurchargeLoadCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.siteRestrictionFreightCost) {
      if (!rateModelBreakdown.siteRestrictionFreightCost) {
        rateModelBreakdown.siteRestrictionFreightCost = 0
      }

      rateModelBreakdown.siteRestrictionFreightCost += ocrm.siteRestrictionFreightCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.siteRestrictionLoadCost) {
      if (!rateModelBreakdown.siteRestrictionLoadCost) {
        rateModelBreakdown.siteRestrictionLoadCost = 0
      }

      rateModelBreakdown.siteRestrictionLoadCost += ocrm.siteRestrictionLoadCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.siteRestrictionTimeCost) {
      if (!rateModelBreakdown.siteRestrictionTimeCost) {
        rateModelBreakdown.siteRestrictionTimeCost = 0
      }

      rateModelBreakdown.siteRestrictionTimeCost += ocrm.siteRestrictionTimeCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.ticketRejectLoadCost) {
      if (!rateModelBreakdown.ticketRejectLoadCost) {
        rateModelBreakdown.ticketRejectLoadCost = 0
      }

      rateModelBreakdown.ticketRejectLoadCost += ocrm.ticketRejectLoadCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.shiftLoadCost) {
      if (!rateModelBreakdown.shiftLoadCost) {
        rateModelBreakdown.shiftLoadCost = 0
      }

      rateModelBreakdown.shiftLoadCost += ocrm.shiftLoadCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.splitLoadCost) {
      if (!rateModelBreakdown.splitLoadCost) {
        rateModelBreakdown.splitLoadCost = 0
      }

      rateModelBreakdown.splitLoadCost += ocrm.splitLoadCost
      rateModelBreakdown.hasSpecificCost = true;
    }

    if (ocrm.wellId) {
      const wellName = ocrm.wellName ?? "Unknown"

      let wellBreakdown: WellBreakdown

      if (wellName in rateModelBreakdown.wellBreakdown) {
        wellBreakdown = rateModelBreakdown.wellBreakdown[wellName]
      } else {
        wellBreakdown = {
          wellId: ocrm.wellId,
          wellName: wellName,
          ocrms: []
        }
        rateModelBreakdown.wellBreakdown[wellName] = wellBreakdown
      }

      wellBreakdown.ocrms.push(getOcrmBreakdown(ocrm))
    } else {
      rateModelBreakdown.nonWellOcrms.push(getOcrmBreakdown(ocrm))
    }

    rateModelBreakdown.ocrms.push(ocrm)
  }

  return breakdown
}

const OcrmRow = (props: { breakdown: OcrmBreakdown }) => {
  const {breakdown} = props

  if (!breakdown.type) {
    return (
      <></>
    )
  }

  return (
    <tr key={breakdown.ocrm.id}>
      <td>
        {breakdown.type}
      </td>
      {breakdown.allocation &&
        <td>
          <FormattedQuantity
            options={{
              ...options,
              style: 'percent'
            }}
            value={breakdown.allocation}
            defaultValue={"N/A"}/>
        </td>
      }
      <td>
        <FormattedQuantity
          options={options}
          value={breakdown.qty}
          defaultValue={"N/A"}/>
      </td>
      <td>
        <FormattedCurrency
          options={options}
          value={breakdown.rate}
          defaultValue={"N/A"}/>
      </td>
      <td>
        <FormattedCurrency
          options={options}
          value={breakdown.cost}
          defaultValue={"N/A"}/>
      </td>
    </tr>
  )
}

const CostPanel = (props: { breakdown: RateModelBreakdown }) => {
  const {breakdown} = props

  return (
    <Grid>
      {Object.keys(breakdown.wellBreakdown).length > 0 &&
        <Grid.Col span={12}>
          <>
            {Object.values(breakdown.wellBreakdown).sort((a, b) => compareString(a.wellName, b.wellName)).map((well) => {
              return (
                <>
                  <Title order={6} my="md">
                    Well: {well.wellName}
                  </Title>
                  <Table>
                    <thead>
                    <tr>
                      <th>
                        Type
                      </th>
                      <th>
                        Allocation
                      </th>
                      <th>
                        Qty
                      </th>
                      <th>
                        Rate
                      </th>
                      <th>
                        Cost
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    {well.ocrms.sort((a, b) => compareString(a.type, b.type)).map((ocrm) => {
                      return <OcrmRow breakdown={ocrm}/>
                    })}
                    </tbody>
                  </Table>
                </>
              )
            })}
            <Divider my="sm" variant="dotted"/>
          </>
        </Grid.Col>
      }
      {breakdown.nonWellOcrms.length > 0 &&
        <Grid.Col span={12}>
          <>
            <Table>
              <thead>
              <tr>
                <th>
                  Type
                </th>
                <th>
                  Qty
                </th>
                <th>
                  Rate
                </th>
                <th>
                  Cost
                </th>
              </tr>
              </thead>
              <tbody>
              {breakdown.nonWellOcrms.sort((a, b) => compareString(a.type, b.type)).map((ocrm) => {
                return <OcrmRow breakdown={ocrm}/>
              })}
              </tbody>
            </Table>
            <Divider my="sm" variant="dotted"/>
          </>
        </Grid.Col>
      }
      <Grid.Col span={12}>
        <>
          <Text ta="left" fz="sm" weight={700} mt="md">
            Total Cost
          </Text>
          <Group spacing="xs">
            <FormattedCurrency
              weight={600}
              value={sum(breakdown.ocrms, value => value.totalCost)}
              options={options}
            />
          </Group>
        </>
      </Grid.Col>
      {breakdown.hasSpecificCost &&
        <>
          <Grid.Col span={12}>
            <Divider label="Specific Costs" variant="dashed" mt="md"/>
          </Grid.Col>
          {breakdown.genericTimeCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Generic Time Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.genericTimeCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.genericFreightCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Generic Time Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.genericFreightCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.genericLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Generic Time Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.genericLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.pickupDemurrageCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Pickup Demurrage Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.pickupDemurrageCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.deliveryDemurrageCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Delivery Demurrage Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.deliveryDemurrageCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.baseFuelSurchargeLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Base Fuel Surcharge Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.baseFuelSurchargeLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.percentFuelSurchargeLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Percent Fuel Surcharge Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.percentFuelSurchargeLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.siteRestrictionFreightCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Site Restriction Freight Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.siteRestrictionFreightCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.siteRestrictionLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Site Restriction Load Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.siteRestrictionLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.siteRestrictionTimeCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Site Restriction Load Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.siteRestrictionTimeCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.ticketRejectLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Reject Load Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.ticketRejectLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.shiftLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Shift Load Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.shiftLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
          {breakdown.splitLoadCost &&
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Split Load Cost
              </Text>
              <Group spacing="xs">
                <FormattedCurrency
                  value={breakdown.splitLoadCost}
                  options={options}
                />
              </Group>
            </Grid.Col>
          }
        </>
      }
    </Grid>
  )
}

export type CostDetailsModal = ModalProps & {
  value: CostedEntity
}

export const CostDetailsModal = (props: CostDetailsModal) => {
  const {value, ...rest} = props

  const costBreakdown: CostBreakdown = useMemo(() => {
    if (value.objectCostRateModels) {
      return getCostBreakdown(value.objectCostRateModels)
    } else {
      return {}
    }
  }, [value.objectCostRateModels])

  const rateModels: string[] = useMemo(() => {
    return Object.keys(costBreakdown)
  }, [costBreakdown])

  return (
    <Modal {...rest}>

      {rateModels.length > 0 ? (
        <Tabs defaultValue={firstOrUndefined(rateModels)}>
          <Tabs.List>
            {rateModels.map((rm) => {
              return (
                <Tabs.Tab value={rm}>{rm}</Tabs.Tab>
              )
            })}
          </Tabs.List>

          {rateModels.map((rm) => {
            return (
              <Tabs.Panel value={rm} pt="xs">
                <CostPanel breakdown={costBreakdown[rm]}/>
              </Tabs.Panel>
            )
          })}
        </Tabs>
      ) : (
        <Center m="lg">
          <Group spacing="xs">
            <IconCoinOff/>
            <Text>No Cost</Text>
          </Group>
        </Center>
      )}
    </Modal>
  )
}
